import { type PickOneGroup, type PickOneKey, pickOneMessage } from './mcp-tool-pick-one'
import {
  describeTool,
  groupMates,
  inputsWithin,
  type McpToolInput,
  type McpToolSchema,
  REQUIRED_INPUT_MESSAGE,
} from './mcp-tool-schema'

export interface MissingInput {
  key: string
  message: string
}

const isFilled = (value: unknown) => typeof value === 'string' && value.trim() !== ''

const requiredWhen = (activator: McpToolInput) => `Required when ${activator.label} is filled`

const pickOneIssues = (
  group: PickOneGroup,
  inputs: McpToolInput[],
  filled: (input: McpToolInput) => boolean,
): MissingInput[] => {
  const anyFilledWithin = (path: string[]) => inputsWithin(inputs, path).some(filled)
  const activator = group.activatedBy && inputsWithin(inputs, group.activatedBy).find(filled)
  if (group.activatedBy && !activator) return []
  const present = (member: PickOneKey) =>
    (member.container && group.requiredKeys.includes(member.path.at(-1))) ||
    anyFilledWithin(member.path)
  // Point at what the builder filled, else at the first thing the member needs.
  const fieldOf = (member: PickOneKey) => {
    const under = inputsWithin(inputs, member.path)
    const needed = under.find((input) => input.required || input.requiredWith)
    return under.find(filled) ?? needed ?? under[0]
  }
  const flag = (members: (PickOneKey | undefined)[], message: string) =>
    members.flatMap((member) => {
      const field = member && fieldOf(member)
      return field ? [{ key: field.key, message }] : []
    })
  const sharedMessage = activator ? requiredWhen(activator) : REQUIRED_INPUT_MESSAGE
  const issues = flag(
    group.shared.filter((member) => !present(member)),
    sharedMessage,
  )
  const satisfied = group.alternatives.filter((members) => members.every(present))
  if (!satisfied.length) {
    issues.push(
      ...flag(
        group.alternatives.map(([first]) => first),
        pickOneMessage(group),
      ),
    )
  } else if (group.exclusive && satisfied.length > 1) {
    const message = `Fill only one of: ${group.choices}`
    issues.push(
      ...flag(
        satisfied.map(([first]) => first),
        message,
      ),
    )
  }
  return issues
}

/** Each input the call cannot go without, given what is filled, with the message to show. */
export const missingInputs = (
  schema: McpToolSchema | null | undefined,
  values: Record<string, unknown>,
): MissingInput[] => {
  const { inputs, pickOnes } = describeTool(schema)
  const filled = (input: McpToolInput) => isFilled(values[input.key])
  const issues = inputs.flatMap((input): MissingInput[] => {
    if (filled(input)) return []
    if (input.required) return [{ key: input.key, message: REQUIRED_INPUT_MESSAGE }]
    const activator = groupMates(input, inputs).find(filled)
    return activator ? [{ key: input.key, message: requiredWhen(activator) }] : []
  })
  const flagged = new Set(issues.map((issue) => issue.key))
  for (const issue of pickOnes.flatMap((group) => pickOneIssues(group, inputs, filled))) {
    if (flagged.has(issue.key)) continue
    flagged.add(issue.key)
    issues.push(issue)
  }
  return issues
}
